import { useEffect, useId, useRef, type ReactNode } from "react";

/**
 * A question before something that cannot be undone (0.24.0): a modal dialog,
 * Escape or the backdrop is "no". The safe choice has the focus.
 */
export function ConfirmDialog({
  eyebrow,
  title,
  children,
  confirm,
  cancel,
  danger = false,
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  eyebrow?: string;
  title: string;
  children?: ReactNode;
  confirm: string;
  cancel: string;
  danger?: boolean;
  busy?: boolean;
  /** The confirm button only (a form not filled in yet). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (typeof d.showModal === "function" && !d.open) d.showModal();
    else d.setAttribute("open", "");
    keep.current?.focus();
    return () => {
      if (d.open && typeof d.close === "function") d.close();
    };
  }, []);

  return (
    <dialog
      ref={dialog}
      className="confirm-dialog"
      role="alertdialog"
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onCancel();
      }}
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}
    >
      <div className="confirm-panel">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h2 id={titleId} className="panel-title">
          {title}
        </h2>
        {children}
        <div className="confirm-actions">
          <button ref={keep} type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>
            {cancel}
          </button>
          <button type="button" className={`btn ${danger ? "btn-secondary btn-danger" : "btn-primary"}`} disabled={busy || confirmDisabled} onClick={onConfirm}>
            {confirm}
          </button>
        </div>
      </div>
    </dialog>
  );
}
